import axios from 'axios';
import { LogLevel } from '@signage-player/contracts';
import { sanitizeForLogging } from './LogSanitizer';
/**
 * Axios wrapper for HTTP requests in main process
 */
export class AxiosWrapper {
    constructor(baseURL, logger, token, options) {
        this.baseURL = baseURL;
        this.doNotLogResponseForEndpoints = [
            'api/v1/devices/*/eventlist', // large payloads
            'api/v1/devices/*/all-eventlists', // large payloads
            'api/v1/devices/*/get-by-hash', // large payloads
        ];
        this.client = axios.create({
            baseURL,
            timeout: 30000, // 30 second timeout
            headers: token
                ? {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`,
                }
                : {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
        });
        this.host = AxiosWrapper.extractHost(baseURL);
        this.logger = logger;
        this.logRequestContext = options?.logRequestContext ?? true;
        this.logResponseContext = options?.logResponseContext ?? true;
        this.setupInterceptors();
    }
    /**
     * Setup request and response interceptors for logging
     */
    setupInterceptors() {
        // Request interceptor
        this.client.interceptors.request.use(config => {
            if (this.logger) {
                this.logger.debug('[AxiosWrapper] Request:', sanitizeForLogging(this.logRequestContext
                    ? {
                        method: config.method?.toUpperCase(),
                        url: config.url,
                        baseURL: config.baseURL,
                        headers: config.headers,
                        data: config.data,
                    }
                    : {
                        method: config.method?.toUpperCase(),
                        url: config.url,
                        baseURL: null,
                        headers: null,
                        data: null,
                    }));
            }
            return config;
        }, error => {
            if (this.logger) {
                this.logger.error('[AxiosWrapper] Request Error:', sanitizeForLogging({ error }));
            }
            return Promise.reject(error);
        });
        // Response interceptor
        this.client.interceptors.response.use(response => {
            if (this.logger) {
                const shouldLogResponse = !this.doNotLogResponseForEndpoints.some(endpointPattern => {
                    const regexPattern = new RegExp('^' + endpointPattern.replace(/\*/g, '.*') + '$');
                    return regexPattern.test(response.config.url || '');
                });
                this.logger.debug('[AxiosWrapper] Response:', sanitizeForLogging(this.logResponseContext
                    ? {
                        status: response.status,
                        statusText: response.statusText,
                        url: response.config.url,
                        method: response.config.method?.toUpperCase(),
                        headers: response.headers,
                        data: shouldLogResponse ? response.data : '[OMITTED]',
                    }
                    : {
                        status: response.status,
                        statusText: null,
                        url: response.config.url,
                        method: null,
                        headers: null,
                        data: null,
                    }));
            }
            return response;
        }, (error) => {
            const isConnectionError = !error.response || error.code === 'ECONNREFUSED' || error.code === 'EAI_AGAIN';
            if (this.logger) {
                const logLevel = isConnectionError ? LogLevel.NOTICE : LogLevel.ERROR;
                this.logger.log(logLevel, '[AxiosWrapper] Response Error:', sanitizeForLogging(this.logResponseContext
                    ? {
                        message: error.message,
                        code: error.code,
                        request: error.config && !isConnectionError
                            ? {
                                method: error.config.method?.toUpperCase(),
                                url: error.config.url,
                                baseURL: error.config.baseURL,
                                headers: error.config.headers,
                                data: error.config.data,
                            }
                            : null,
                        response: error.response && !isConnectionError
                            ? {
                                status: error.response.status,
                                statusText: error.response.statusText,
                                headers: error.response.headers,
                                data: error.response.data,
                            }
                            : null,
                        stack: error.stack && !isConnectionError ? error.stack : null,
                        status: null,
                        url: null,
                    }
                    : {
                        message: error.message,
                        code: error.code,
                        status: error.response?.status,
                        url: error.config?.url,
                        request: null,
                        response: null,
                        stack: null,
                    }));
            }
            // Fire the injected connection-error probe (DNS / resolver / open-FD
            // diagnostics). Fully isolated: it must never alter the request outcome.
            if (isConnectionError && this.connectionErrorProbe) {
                try {
                    this.connectionErrorProbe({ host: this.host, code: error.code ?? 'UNKNOWN', url: error.config?.url });
                }
                catch {
                    /* diagnostics must never affect the request path */
                }
            }
            return Promise.reject(error);
        });
    }
    /**
     * Update log options at runtime (e.g. from dynamic config overrides)
     */
    setLogOptions(options) {
        if (options.logRequestContext !== undefined) {
            this.logRequestContext = options.logRequestContext;
        }
        if (options.logResponseContext !== undefined) {
            this.logResponseContext = options.logResponseContext;
        }
    }
    /**
     * Set logger instance for request/response logging
     */
    setLogger(logger) {
        this.logger = logger;
    }
    /**
     * Inject a diagnostic probe fired on connection-level failures (DNS / refused /
     * timeout). Electron main process only; best-effort and isolated from the
     * request path. See createNetworkDiagnosticsProbe in the electron app.
     */
    setConnectionErrorProbe(probe) {
        this.connectionErrorProbe = probe;
    }
    static extractHost(baseURL) {
        try {
            return new URL(baseURL).hostname;
        }
        catch {
            return '';
        }
    }
    buildConfig(config = {}) {
        return {
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            ...config,
        };
    }
    get(uri, config = {}) {
        return this.client.get(this.buildUri(uri), this.buildConfig(config));
    }
    post(uri, data, config = {}) {
        return this.client.post(this.buildUri(uri), data, this.buildConfig(config));
    }
    put(uri, data, config = {}) {
        return this.client.put(this.buildUri(uri), data, this.buildConfig(config));
    }
    delete(uri, config = {}) {
        return this.client.delete(this.buildUri(uri), this.buildConfig(config));
    }
    buildUri(uri) {
        return `${uri}`; // Future: add query parameters like screenId
    }
}
export default AxiosWrapper;
//# sourceMappingURL=AxiosWrapper.js.map